import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save } from 'lucide-react';
import { api, adminApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const ContentEditor = () => {
  const [siteData, setSiteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const normalizeSiteData = (raw) => ({
    ...raw,
    siteMeta: {
      documentTitleFr:
        raw.siteMeta?.documentTitleFr ??
        'Loranrix — Portfolio développeur full-stack',
      documentTitleEn:
        raw.siteMeta?.documentTitleEn ??
        'Loranrix — Full-stack developer portfolio',
    },
  });

  const loadData = async () => {
    try {
      const response = await api.getSiteData();
      setSiteData(normalizeSiteData(response.data));
    } catch (error) {
      toast({ title: 'Error loading data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateSiteData(siteData);
      toast({ title: 'Content saved successfully!' });
    } catch (error) {
      toast({ title: 'Error saving content', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const updateField = (lang, section, field, value) => {
    setSiteData(prev => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [section]: {
          ...prev[lang][section],
          [field]: value
        }
      }
    }));
  };

  const updateSiteMeta = (field, value) => {
    setSiteData((prev) => ({
      ...prev,
      siteMeta: { ...prev.siteMeta, [field]: value },
    }));
  };

  const updateStat = (lang, index, field, value) => {
    const newStats = [...siteData[lang].about.stats];
    newStats[index] = { ...newStats[index], [field]: value };
    setSiteData(prev => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        about: { ...prev[lang].about, stats: newStats }
      }
    }));
  };

  if (loading) return <div className="p-8 text-white">Loading...</div>;

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Content Editor</h1>
          <p className="text-gray-400">Edit site texts and numbers</p>
        </div>
        <motion.button
          onClick={handleSave}
          disabled={saving}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-semibold flex items-center gap-2"
        >
          <Save className="w-5 h-5" />
          {saving ? 'Saving...' : 'Save Changes'}
        </motion.button>
      </div>

      <div className="space-y-8">
        <div className="bg-gradient-to-br from-[#1a1a1b] to-[#0f0f10] rounded-xl border border-cyan-500/10 p-6">
          <h2 className="text-2xl font-bold text-cyan-400 mb-2">Titre de l&apos;onglet (navigateur)</h2>
          <p className="text-gray-400 text-sm mb-6">
            Texte affiché dans l&apos;onglet ; selon la langue FR/EN choisie par le visiteur.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-400 mb-2">Français</label>
              <input
                type="text"
                value={siteData.siteMeta.documentTitleFr}
                onChange={(e) => updateSiteMeta('documentTitleFr', e.target.value)}
                className="w-full px-4 py-2 bg-[#0a0a0b] border border-cyan-500/20 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-gray-400 mb-2">English</label>
              <input
                type="text"
                value={siteData.siteMeta.documentTitleEn}
                onChange={(e) => updateSiteMeta('documentTitleEn', e.target.value)}
                className="w-full px-4 py-2 bg-[#0a0a0b] border border-cyan-500/20 rounded-lg text-white"
              />
            </div>
          </div>
        </div>

        {['fr', 'en'].map(lang => (
          <div key={lang} className="bg-gradient-to-br from-[#1a1a1b] to-[#0f0f10] rounded-xl border border-cyan-500/10 p-6">
            <h2 className="text-2xl font-bold text-cyan-400 mb-6">{lang === 'fr' ? 'Français' : 'English'}</h2>
            
            {/* Hero Section */}
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-white mb-4">Hero Section</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-400 mb-2">Title</label>
                  <input
                    type="text"
                    value={siteData[lang].hero.title}
                    onChange={(e) => updateField(lang, 'hero', 'title', e.target.value)}
                    className="w-full px-4 py-2 bg-[#0a0a0b] border border-cyan-500/20 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 mb-2">Title Highlight</label>
                  <input
                    type="text"
                    value={siteData[lang].hero.titleHighlight}
                    onChange={(e) => updateField(lang, 'hero', 'titleHighlight', e.target.value)}
                    className="w-full px-4 py-2 bg-[#0a0a0b] border border-cyan-500/20 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 mb-2">Subtitle</label>
                  <textarea
                    value={siteData[lang].hero.subtitle}
                    onChange={(e) => updateField(lang, 'hero', 'subtitle', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 bg-[#0a0a0b] border border-cyan-500/20 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 mb-2">Badge hero (ex. freelance)</label>
                  <input
                    type="text"
                    value={siteData[lang].hero.availabilityBadge ?? ''}
                    onChange={(e) => updateField(lang, 'hero', 'availabilityBadge', e.target.value)}
                    placeholder={lang === 'fr' ? 'Disponible en freelance' : 'Available for freelance'}
                    className="w-full px-4 py-2 bg-[#0a0a0b] border border-cyan-500/20 rounded-lg text-white"
                  />
                </div>
              </div>
            </div>

            {/* Footer (tagline, copyright) */}
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-white mb-4">Footer</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-400 mb-2">Tagline</label>
                  <input
                    type="text"
                    value={siteData[lang].footer.tagline}
                    onChange={(e) => updateField(lang, 'footer', 'tagline', e.target.value)}
                    className="w-full px-4 py-2 bg-[#0a0a0b] border border-cyan-500/20 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 mb-2">Copyright</label>
                  <input
                    type="text"
                    value={siteData[lang].footer.copyright}
                    onChange={(e) => updateField(lang, 'footer', 'copyright', e.target.value)}
                    className="w-full px-4 py-2 bg-[#0a0a0b] border border-cyan-500/20 rounded-lg text-white"
                  />
                </div>
              </div>
            </div>

            {/* About Section Stats */}
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-white mb-4">Stats (Numbers)</h3>
              <div className="grid grid-cols-3 gap-4">
                {siteData[lang].about.stats.map((stat, i) => (
                  <div key={i} className="space-y-2">
                    <input
                      type="text"
                      value={stat.number}
                      onChange={(e) => updateStat(lang, i, 'number', e.target.value)}
                      placeholder="Number"
                      className="w-full px-4 py-2 bg-[#0a0a0b] border border-cyan-500/20 rounded-lg text-white"
                    />
                    <input
                      type="text"
                      value={stat.label}
                      onChange={(e) => updateStat(lang, i, 'label', e.target.value)}
                      placeholder="Label"
                      className="w-full px-4 py-2 bg-[#0a0a0b] border border-cyan-500/20 rounded-lg text-white"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}

        {/* Socials */}
        <div className="bg-gradient-to-br from-[#1a1a1b] to-[#0f0f10] rounded-xl border border-cyan-500/10 p-6">
          <h2 className="text-2xl font-bold text-cyan-400 mb-6">Social Media Links</h2>
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(siteData.socials).map(([platform, url]) => (
              <div key={platform}>
                <label className="block text-gray-400 mb-2 capitalize">{platform}</label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setSiteData(prev => ({
                    ...prev,
                    socials: { ...prev.socials, [platform]: e.target.value }
                  }))}
                  placeholder={`https://${platform}.com/username`}
                  className="w-full px-4 py-2 bg-[#0a0a0b] border border-cyan-500/20 rounded-lg text-white"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentEditor;
